/**
 * Security Alerts Page (Admin Only)
 *
 * Security Design (for viva):
 * - Alerts are stored persistently in the backend security_alerts table
 * - Lifecycle: NEW → SEEN → REVIEWED → RESOLVED
 *   · NEW:      Alert just created (counts toward badge)
 *   · SEEN:     Admin opened alert list (badge clears automatically)
 *   · REVIEWED: Admin explicitly clicked "Review" on a specific alert
 *   · RESOLVED: Admin resolved/closed the incident
 * - Alerts are NEVER auto-reviewed — admin must manually action them
 * - This ensures every security incident has explicit human acknowledgment
 * - Export to PDF for incident documentation
 */

import React, { useState, useEffect, useMemo } from 'react';
import { getSecurityAlerts, reviewAlert, resolveAlert } from '../services/api';
import { formatDateTime as tzFormatDateTime } from '../utils/time';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

function SecurityAlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showResolved, setShowResolved] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    loadAlerts();
  }, []);

  const loadAlerts = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await getSecurityAlerts();
      setAlerts(data || []);
      // After opening alert list, NEW alerts become SEEN on backend.
      // Notify App.js to refresh the badge count (should now be 0).
      window.dispatchEvent(new Event('alertsReviewed'));
    } catch (err) {
      console.error('[SecurityAlerts] Failed to load:', err);
      setError(err.message || 'Failed to load security alerts');
      setAlerts([]);
    } finally {
      setLoading(false);
      setLastRefresh(new Date());
    }
  };

  // ── Admin actions ──────────────────────────────────────────────

  const handleReview = async (alertId) => {
    setActionLoading(prev => ({ ...prev, [alertId]: 'review' }));
    try {
      await reviewAlert(alertId);
      // Update local state immediately
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: 'REVIEWED' } : a));
    } catch (err) {
      console.error('[SecurityAlerts] Review failed:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [alertId]: null }));
    }
  };

  const handleResolve = async (alertId) => {
    setActionLoading(prev => ({ ...prev, [alertId]: 'resolve' }));
    try {
      await resolveAlert(alertId);
      setAlerts(prev => prev.map(a => a.id === alertId ? { ...a, status: 'RESOLVED' } : a));
    } catch (err) {
      console.error('[SecurityAlerts] Resolve failed:', err);
    } finally {
      setActionLoading(prev => ({ ...prev, [alertId]: null }));
    }
  };

  // ── Helpers ────────────────────────────────────────────────────

  const getSeverityColor = (sev) => {
    switch (sev) { case 'HIGH': return 'red'; case 'MEDIUM': return 'yellow'; case 'LOW': return 'blue'; default: return 'blue'; }
  };
  const getSeverityBorder = (sev) => {
    switch (sev) { case 'HIGH': return 'var(--red)'; case 'MEDIUM': return 'var(--yellow)'; case 'LOW': return 'var(--blue)'; default: return 'var(--text-secondary)'; }
  };
  const getSeverityIcon = (sev) => {
    if (sev === 'HIGH') return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--red)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
        <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
      </svg>
    );
    if (sev === 'MEDIUM') return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--yellow)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
    );
    return (
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--blue)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
      </svg>
    );
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'NEW': return <span className="security-badge red" style={s.badge}>NEW</span>;
      case 'SEEN': return <span className="security-badge yellow" style={s.badge}>SEEN</span>;
      case 'REVIEWED': return <span className="security-badge blue" style={s.badge}>REVIEWED</span>;
      case 'RESOLVED': return <span className="security-badge green" style={s.badge}>RESOLVED</span>;
      default: return null;
    }
  };

  // ── Filtering ──────────────────────────────────────────────────

  const filteredAlerts = useMemo(() => {
    let result = alerts;
    if (filterSeverity) result = result.filter(a => a.severity === filterSeverity);
    if (filterStatus) result = result.filter(a => a.status === filterStatus);
    return result;
  }, [alerts, filterSeverity, filterStatus]);

  const activeAlerts = filteredAlerts.filter(a => a.status !== 'RESOLVED');
  const resolvedAlerts = filteredAlerts.filter(a => a.status === 'RESOLVED');

  // Counts for active (non-resolved) alerts by severity
  const counts = useMemo(() => {
    const c = { HIGH: 0, MEDIUM: 0, LOW: 0 };
    alerts.filter(a => a.status !== 'RESOLVED').forEach(a => {
      c[a.severity] = (c[a.severity] || 0) + 1;
    });
    return c;
  }, [alerts]);

  // Threat level (dark theme colors)
  const threatLevel = counts.HIGH > 0 ? 'CRITICAL' : counts.MEDIUM > 0 ? 'CAUTION' : 'SAFE';
  const threatColor = threatLevel === 'CRITICAL' ? 'var(--red)' : threatLevel === 'CAUTION' ? 'var(--yellow)' : 'var(--green)';

  // ── Export ─────────────────────────────────────────────────────

  const exportToPDF = () => {
    const doc = new jsPDF();
    const now = new Date().toLocaleString('en-IE');
    doc.setFontSize(18);
    doc.setTextColor(15, 23, 42);
    doc.text('JOSMY - Security Alerts Report', 14, 18);
    doc.setFontSize(10);
    doc.setTextColor(100, 116, 139);
    doc.text(`Generated: ${now}`, 14, 25);
    doc.text(`Threat Level: ${threatLevel}`, 14, 30);
    doc.text(`Active: ${activeAlerts.length} | Resolved: ${resolvedAlerts.length}`, 14, 35);

    const headers = [['#', 'Severity', 'Alert Type', 'Description', 'User', 'Timestamp', 'Status']];
    const rows = filteredAlerts.map((a, i) => [
      i + 1, a.severity, a.alert_type.replace(/_/g, ' '),
      a.description.substring(0, 80) + (a.description.length > 80 ? '...' : ''),
      a.user_email || a.user_id || '—', tzFormatDateTime(a.timestamp),
      a.status,
    ]);

    autoTable(doc, {
      head: headers, body: rows, startY: 40,
      styles: { fontSize: 8, cellPadding: 2 },
      headStyles: { fillColor: [15, 23, 42], textColor: 255, fontStyle: 'bold' },
      alternateRowStyles: { fillColor: [248, 250, 252] },
    });

    doc.setFontSize(8);
    doc.setTextColor(148, 163, 184);
    doc.text('JOSMY Security Alerts | CONFIDENTIAL', doc.internal.pageSize.getWidth() / 2, doc.internal.pageSize.getHeight() - 10, { align: 'center' });
    doc.save(`JOSMY_Security_Alerts_${new Date().toISOString().split('T')[0]}.pdf`);
  };

  // ── Render ─────────────────────────────────────────────────────

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <div style={{ width: 28, height: 28, borderWidth: 3, borderStyle: 'solid', borderColor: 'var(--text-muted)', borderTopColor: 'var(--blue)', borderRadius: '50%', animation: 'spin 0.7s linear infinite' }} />
      </div>
    );
  }

  return (
    <div className="page-container admin-page-shell admin-tactile-page">
      <style>{`
        .security-alert-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(15,23,42,0.08), 0 6px 16px rgba(99,102,241,0.06);
        }
        .security-refresh-btn:hover { background-color: var(--border); border-color: var(--border-hover); }
        .security-export-btn:hover { background-color: #334155; border-color: rgba(255,255,255,0.15); }
        .security-review-btn:hover { background-color: rgba(99,102,241,0.2); border-color: rgba(99,102,241,0.5); }
        .security-resolve-btn:hover { background-color: var(--green-muted); border-color: rgba(34,197,94,0.5); }
      `}</style>
      {/* Header */}
      <div style={s.headerRow}>
        <div>
          <h2 style={s.pageTitle}>Security Alerts</h2>
          <p style={s.subtitle}>
            Persistent alerts from security pattern analysis. Lifecycle: NEW → SEEN → REVIEWED → RESOLVED.
            <strong> Review</strong> marks that you have assessed the incident; <strong>Resolve</strong> closes it after remediation.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={s.updatedText} title="Browser local time when this list was loaded">
            Data fetched: {lastRefresh.toLocaleTimeString('en-IE')} (local)
          </span>
          <button onClick={loadAlerts} style={s.refreshBtn} className="security-refresh-btn" title="Refresh">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
            </svg>
          </button>
          <button onClick={exportToPDF} style={s.exportBtn} className="security-export-btn">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
            </svg>
            Export PDF
          </button>
        </div>
      </div>

      {/* Threat Level Banner */}
      <div style={{ ...s.threatBanner, borderColor: threatColor, boxShadow: `0 0 24px ${threatColor}33` }}>
        <div style={s.threatBannerCenter}>
          <div style={s.threatBannerIcon}>
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke={threatColor} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
            </svg>
          </div>
          <div style={{ ...s.threatBannerLabel, color: threatColor }}>System Threat Level: {threatLevel}</div>
          <div style={s.threatBannerSubtext}>
            {threatLevel === 'CRITICAL' ? 'Immediate attention required — HIGH severity alerts detected' :
             threatLevel === 'CAUTION' ? 'Some alerts need review — no critical threats' :
             'All clear — no active security alerts'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
          {counts.HIGH > 0 && <span className="security-badge red" style={s.badge}>{counts.HIGH} HIGH</span>}
          {counts.MEDIUM > 0 && <span className="security-badge yellow" style={s.badge}>{counts.MEDIUM} MEDIUM</span>}
          {counts.LOW > 0 && <span className="security-badge blue" style={s.badge}>{counts.LOW} LOW</span>}
          {activeAlerts.length === 0 && <span className="security-badge green" style={s.badge}>ALL CLEAR</span>}
        </div>
      </div>

      {/* Filters */}
      <div style={s.filterRow}>
        <select value={filterSeverity} onChange={(e) => setFilterSeverity(e.target.value)} style={s.select}>
          <option value="">All Severities</option>
          <option value="HIGH">HIGH</option>
          <option value="MEDIUM">MEDIUM</option>
          <option value="LOW">LOW</option>
        </select>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={s.select}>
          <option value="">All Statuses</option>
          <option value="NEW">NEW</option>
          <option value="SEEN">SEEN</option>
          <option value="REVIEWED">REVIEWED</option>
          <option value="RESOLVED">RESOLVED</option>
        </select>
        <label style={s.toggleLabel}>
          <input type="checkbox" checked={showResolved} onChange={(e) => setShowResolved(e.target.checked)} style={{ marginRight: 6 }} />
          Show resolved ({resolvedAlerts.length})
        </label>
      </div>

      <p style={s.actionHint}>
        <strong>Review</strong> — acknowledge and record that this alert was examined (status → REVIEWED).
        {' '}
        <strong>Resolve</strong> — close the incident when handled (status → RESOLVED). Opening this page marks unseen alerts as SEEN (badge clears).
      </p>

      {/* Error */}
      {error && (
        <div style={s.errorBox}>
          <strong>Error:</strong> {error}
          <button onClick={loadAlerts} style={s.retryBtn}>Retry</button>
        </div>
      )}

      {/* Active Alerts */}
      {activeAlerts.length === 0 && !showResolved ? (
        <div style={s.noAlerts}>
          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
          </svg>
          <div style={{ marginTop: 10 }}>
            <span className="security-badge green" style={s.badge}>ALL CLEAR</span>
          </div>
          <p style={s.noAlertsText}>
            {resolvedAlerts.length > 0 ? 'All alerts have been resolved.' : 'No security alerts at this time.'}
          </p>
        </div>
      ) : (
        <div style={s.alertList}>
          {activeAlerts.map((alert) => (
            <AlertCard
              key={alert.id} alert={alert}
              getSeverityIcon={getSeverityIcon} getSeverityColor={getSeverityColor} getSeverityBorder={getSeverityBorder}
              getStatusBadge={getStatusBadge}
              onReview={() => handleReview(alert.id)}
              onResolve={() => handleResolve(alert.id)}
              actionLoading={actionLoading[alert.id]}
            />
          ))}
        </div>
      )}

      {/* Resolved */}
      {showResolved && resolvedAlerts.length > 0 && (
        <>
          <h3 style={{ ...s.sectionTitle, marginTop: 24 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--green)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: 8 }}>
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            Resolved Alerts ({resolvedAlerts.length})
          </h3>
          <div style={s.alertList}>
            {resolvedAlerts.map((alert) => (
              <AlertCard
                key={alert.id} alert={alert}
                getSeverityIcon={getSeverityIcon} getSeverityColor={getSeverityColor} getSeverityBorder={getSeverityBorder}
                getStatusBadge={getStatusBadge}
                isResolved
              />
            ))}
          </div>
        </>
      )}

      {/* Alert Policy */}
      <div style={s.policyBox}>
        <div style={s.policyHeader}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          <strong>Alert Detection Rules</strong>
        </div>
        <div style={s.ruleList}>
          <RuleItem badge="red" title="Brute Force Suspected" desc="3+ failed OTP attempts by one user within a short period" />
          <RuleItem badge="red" title="Unusual Login Location" desc="Login from a new IP range not seen before for the user" />
          <RuleItem badge="yellow" title="Multiple OTP Failures" desc="2 failed OTP attempts by one user" />
          <RuleItem badge="yellow" title="Rapid Decrypt Requests" desc="5+ decrypt requests in 10 minutes from one user" />
          <RuleItem badge="blue" title="Dormant Account Access" desc="Login after 30+ days of inactivity" />
          <RuleItem badge="blue" title="High Upload Volume" desc="20+ file uploads by one user in 24 hours" />
        </div>
      </div>
    </div>
  );
}


function AlertCard({ alert, getSeverityIcon, getSeverityColor, getSeverityBorder, getStatusBadge, onReview, onResolve, actionLoading, isResolved }) {
  return (
    <div style={{
      ...s.alertCard,
      borderLeftColor: getSeverityBorder(alert.severity),
      opacity: isResolved ? 0.65 : 1,
    }} className="security-alert-card">
      <div style={s.alertHeader}>
        {getSeverityIcon(alert.severity)}
        <span className={`security-badge ${getSeverityColor(alert.severity)}`} style={s.badge}>{alert.severity}</span>
        <span style={s.alertType}>{alert.alert_type.replace(/_/g, ' ')}</span>
        {getStatusBadge(alert.status)}
        {alert.timestamp && (
          <span style={s.alertTime}>{tzFormatDateTime(alert.timestamp)}</span>
        )}
      </div>
      <p style={s.alertDesc}>{alert.description}</p>
      <div style={s.alertFooter}>
        {alert.user_email && (
          <div style={s.alertMeta}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
            </svg>
            <span>{alert.user_email}</span>
          </div>
        )}
        {alert.ip_address && (
          <div style={s.alertMeta}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--text-secondary)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="2" width="20" height="8" rx="2" ry="2"/><rect x="2" y="14" width="20" height="8" rx="2" ry="2"/>
              <line x1="6" y1="6" x2="6.01" y2="6"/><line x1="6" y1="18" x2="6.01" y2="18"/>
            </svg>
            <span>{alert.ip_address}</span>
          </div>
        )}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          {/* Lifecycle info */}
          {alert.reviewed_by && (
            <span style={s.alertMetaMuted}>
              Reviewed by {alert.reviewed_by}{alert.reviewed_at ? ` at ${tzFormatDateTime(alert.reviewed_at)}` : ''}
            </span>
          )}
          {alert.resolved_by && (
            <span style={s.alertMetaMuted}>
              Resolved by {alert.resolved_by}{alert.resolved_at ? ` at ${tzFormatDateTime(alert.resolved_at)}` : ''}
            </span>
          )}

          {/* Action buttons based on current status */}
          {(alert.status === 'NEW' || alert.status === 'SEEN') && onReview && (
            <button
              type="button"
              onClick={onReview}
              disabled={actionLoading === 'review'}
              style={s.reviewBtn}
              className="security-review-btn"
              title="Mark this alert as reviewed (REVIEWED)"
            >
              {actionLoading === 'review' ? 'Reviewing...' : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  Review
                </>
              )}
            </button>
          )}
          {alert.status === 'REVIEWED' && onResolve && (
            <button
              type="button"
              onClick={onResolve}
              disabled={actionLoading === 'resolve'}
              style={s.resolveBtn}
              className="security-resolve-btn"
              title="Close this incident after remediation (RESOLVED)"
            >
              {actionLoading === 'resolve' ? 'Resolving...' : (
                <>
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                    <polyline points="22 4 12 14.01 9 11.01"/>
                  </svg>
                  Resolve
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


function RuleItem({ badge, title, desc }) {
  return (
    <div style={s.ruleItem}>
      <span className={`security-badge ${badge}`} style={{ ...s.badge, flexShrink: 0 }}>{badge === 'red' ? 'HIGH' : badge === 'yellow' ? 'MED' : 'LOW'}</span>
      <span><strong>{title}:</strong> {desc}</span>
    </div>
  );
}


const s = {
  pageTitle: {
    fontSize: 22, fontWeight: 900, margin: '0 0 6px 0', letterSpacing: '-0.4px', lineHeight: 1.1,
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6, #06B6D4)',
    WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
  },
  subtitle: { fontSize: 13, color: 'var(--text-secondary)', margin: 0, lineHeight: 1.5, fontWeight: 500 },
  headerRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, gap: 16, flexWrap: 'wrap' },
  updatedText: { fontSize: 13, color: 'var(--text-muted)', fontWeight: 500 },
  refreshBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 40, height: 40, borderRadius: 14,
    border: '1.5px solid rgba(99,102,241,0.12)', backgroundColor: 'var(--surface-85)',
    cursor: 'pointer', color: 'var(--text-secondary)',
    transition: 'all 0.3s ease', boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },
  exportBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
    borderRadius: 14, border: '1.5px solid rgba(99,102,241,0.12)',
    backgroundColor: 'var(--surface-85)', color: 'var(--text-body)', fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)', boxShadow: '0 2px 8px rgba(99,102,241,0.06)',
  },

  threatBanner: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center',
    padding: '28px 24px', borderRadius: 22, border: '1.5px solid',
    marginBottom: 20, gap: 16, flexWrap: 'wrap',
    backgroundColor: 'var(--surface-85)', backdropFilter: 'blur(12px)',
    boxShadow: '0 4px 24px rgba(99,102,241,0.08)',
  },
  threatBannerCenter: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  threatBannerIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  threatBannerLabel: { fontSize: 20, fontWeight: 900, letterSpacing: '-0.3px' },
  threatBannerSubtext: { fontSize: 14, color: 'var(--text-secondary)', opacity: 0.95, fontWeight: 500 },

  filterRow: { display: 'flex', gap: 14, alignItems: 'center', marginBottom: 20, flexWrap: 'wrap' },
  select: {
    padding: '10px 14px', border: '1.5px solid rgba(99,102,241,0.1)', borderRadius: 14,
    fontSize: 14, backgroundColor: 'var(--surface-90)', color: 'var(--text-primary)',
    fontWeight: 500, transition: 'all 0.2s ease',
  },
  toggleLabel: { fontSize: 13, color: 'var(--text-body)', display: 'flex', alignItems: 'center', cursor: 'pointer', fontWeight: 500 },

  actionHint: {
    fontSize: 12,
    color: 'var(--text-secondary)',
    lineHeight: 1.55,
    margin: '0 0 18px',
    padding: '12px 14px',
    borderRadius: 12,
    backgroundColor: 'var(--surface-85)',
    border: '1.5px solid rgba(99,102,241,0.08)',
    fontWeight: 500,
  },

  errorBox: {
    padding: '14px 18px', backgroundColor: 'var(--red-muted)', border: '1.5px solid rgba(239,68,68,0.3)',
    borderRadius: 16, marginBottom: 16, color: '#fca5a5', fontSize: 14,
  },
  retryBtn: {
    marginLeft: 12, padding: '10px 18px', borderRadius: 14,
    border: '1.5px solid rgba(239,68,68,0.3)', background: 'var(--red-muted)', color: '#fca5a5',
    cursor: 'pointer', fontSize: 14, fontWeight: 700,
  },

  noAlerts: {
    backgroundColor: 'var(--surface-85)', border: '1.5px solid rgba(34,197,94,0.15)',
    padding: '48px 24px', borderRadius: 22, textAlign: 'center', marginBottom: 28,
    backdropFilter: 'blur(12px)', boxShadow: '0 4px 20px rgba(34,197,94,0.06)',
  },
  noAlertsText: { margin: '12px 0 0', color: 'var(--text-secondary)', fontSize: 13, fontWeight: 600 },

  alertList: { display: 'flex', flexDirection: 'column', gap: 18, marginBottom: 28 },
  alertCard: {
    padding: '20px 22px', borderRadius: 20,
    backgroundColor: 'var(--surface-85)',
    borderWidth: 1.5, borderStyle: 'solid', borderColor: 'rgba(99,102,241,0.08)',
    borderLeftWidth: 4, borderLeftStyle: 'solid',
    boxShadow: '0 4px 20px rgba(99,102,241,0.06)',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    backdropFilter: 'blur(12px)',
  },
  alertHeader: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' },
  alertType: { fontSize: 14, fontWeight: 700, color: 'var(--text-body)', letterSpacing: '0.1px' },
  alertTime: { fontSize: 13, color: 'var(--text-muted)', marginLeft: 'auto', fontWeight: 500 },
  alertDesc: { fontSize: 13, color: 'var(--text-primary)', margin: '0 0 12px', lineHeight: 1.5, fontWeight: 500 },
  alertFooter: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  alertMeta: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 },
  alertMetaMuted: { fontSize: 12, color: 'var(--text-muted)' },

  reviewBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
    borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg, #6366F1, #8B5CF6)', color: '#fff',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: '0 3px 12px rgba(99,102,241,0.3)',
  },
  resolveBtn: {
    display: 'flex', alignItems: 'center', gap: 6, padding: '10px 18px',
    borderRadius: 14, border: 'none',
    background: 'linear-gradient(135deg, #22C55E, #16A34A)', color: '#fff',
    fontSize: 14, fontWeight: 700, cursor: 'pointer',
    transition: 'all 0.3s cubic-bezier(0.22,1,0.36,1)',
    boxShadow: '0 3px 12px rgba(34,197,94,0.3)',
  },
  sectionTitle: {
    fontSize: 17, fontWeight: 800, color: 'var(--text-primary)', display: 'flex', alignItems: 'center',
    marginBottom: 14, letterSpacing: '-0.3px',
  },

  policyBox: {
    backgroundColor: 'var(--surface-85)', padding: 22, borderRadius: 20,
    border: '1.5px solid rgba(99,102,241,0.08)', fontSize: 13, color: 'var(--text-secondary)',
    boxShadow: '0 4px 20px rgba(99,102,241,0.06)',
    backdropFilter: 'blur(12px)',
  },
  policyHeader: { display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, fontSize: 14, color: 'var(--text-body)', fontWeight: 700 },
  ruleList: { display: 'flex', flexDirection: 'column', gap: 12 },
  ruleItem: { display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 },
  badge: {}, // badge styles merged where used
};

export default SecurityAlertsPage;
